/**
 * Centralized logging utility
 *
 * In development: logs to console
 * In production: logs to Sentry and optionally console for critical errors
 */

type LogContext = object;

class Logger {
  private isDevelopment = import.meta.env.DEV;

  /**
   * Log informational message (development only)
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, context || '');
    }

    // In production, could send to Sentry as breadcrumb
    if (!this.isDevelopment && window.Sentry) {
      window.Sentry.addBreadcrumb({
        message,
        level: 'warning',
        data: context,
      });
    }
  }

  /**
   * Log error message
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    // Always log errors to console in development
    if (this.isDevelopment) {
      console.error(`[ERROR] ${message}`, error, context || '');
    }

    // Send to Sentry in production
    if (!this.isDevelopment && window.Sentry) {
      if (error instanceof Error) {
        window.Sentry.captureException(error, {
          tags: { message },
          extra: context,
        });
      } else {
        window.Sentry.captureMessage(message, {
          level: 'error',
          extra: { error, ...context },
        });
      }
    }
  }

  /**
   * Log caught exception with user-friendly message
   */
  catchError(userMessage: string, error: Error | unknown, context?: LogContext): void {
    this.error(userMessage, error, context);
  }
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    Sentry?: {
      captureException: (error: Error, context?: { tags?: Record<string, unknown>; extra?: unknown }) => void;
      captureMessage: (message: string, context?: { level?: string; extra?: unknown }) => void;
      addBreadcrumb: (breadcrumb: { message: string; level: string; data?: unknown }) => void;
    };
  }
}

export const logger = new Logger();
